/* eslint-disable indent */
import React, {
  CSSProperties,
  SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import classNames from 'classnames'
import { GenImgAttrsData, getOptimizedImage } from './utils'
import { logger, metrics } from 'lib/slardar-utils'
import ImageStyles from './style'
import batchedUpdates from 'lib/batched-updates'
export { default as ImageZoom } from './image-zoom'
import style from './index.module.scss'

// 常用的一些图片
export const userLoading = require('static/user-loading.svg')
export const carLoading = require('static/car-bg-l.png')
export const logoLoading = require('static/dongchedi-logo.svg')
export const logoLoadingRect = require('static/loading/logo~rect.svg')
export const commentLoading = require('static/comment-loading.svg')
export const smallLogoLoading = require('static/loading/logo-small.svg')
export const dealerLogoLoading = require('static/loading/logo-dealer.svg')

export type ImageProps = {
  /**
   * 图片宽度
   */
  width: number
  /**
   * 图片高度
   */
  height: number
  /**
   * 图片资源地址
   */
  src: string
  /**
   * className
   */
  className?: string
  /**
   * alt
   */
  layout?: 'fill' | 'responsive' | 'intrinsic' | 'fixed'
  alt?: string
  objectFit?: CSSProperties['objectFit']
  objectPosition?: CSSProperties['objectPosition']
  cutType?: GenImgAttrsData['cutType']
  /**
   * 是否懒加载
   */
  lazy?: boolean
  /**
   * 不使用图片格式优化，主要是webp
   * @default false
   */
  unoptimized?: boolean
  /**
   * 是否开启加载完毕后的动画，默认开启
   */
  useTransition?: boolean
  /**
   * hover动画
   */
  hoverTransition?: boolean
  /**
   * intersection observer的rootMargin
   * @default 0px 100px
   */
  rootMargin?: string
  /**
   * 自定义图片的行内样式
   */
  style?: React.CSSProperties
  /**
   * 自定义默认图片
   */
  cusDefaultImg?: string
  /**
   * 自定义错误图片
   */
  errorImg?: string
  /**
   * 是否自动适配 设备像素比 服务器端默认2倍，客户端根据实际的devicePixelRatio
   */
  autoPixelRatio?: boolean
  /**
   * 点击事件
   */
  onClick?(): void
  onLoad?(event: SyntheticEvent<HTMLImageElement, Event>): void
}

type ImgElementStyle = NonNullable<JSX.IntrinsicElements['img']['style']>

const classNamePrefix = 'motor-pc-image'

export const defaultImg =
  'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwMCIgaGVpZ2h0PSIxMDAwIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZlcnNpb249IjEuMSIvPg=='

export const Image: React.FC<ImageProps> = ({
  src,
  width,
  height,
  lazy = true,
  unoptimized,
  cutType,
  layout = 'intrinsic',
  alt,
  objectFit = 'cover',
  objectPosition,
  cusDefaultImg = defaultImg,
  errorImg = defaultImg,
  hoverTransition = false,
  autoPixelRatio = true,
  className,
  onClick,
  onLoad,
}) => {
  const imageStyle: CSSProperties = {
    position: 'absolute',
    top: 0,
    left: 0,
    bottom: 0,
    right: 0,

    boxSizing: 'border-box',
    padding: 0,
    border: 'none',
    margin: 'auto',

    display: 'block',
    width: 0,
    height: 0,
    minWidth: '100%',
    maxWidth: '100%',
    minHeight: '100%',
    maxHeight: '100%',

    objectFit,
    objectPosition,
  }

  const wrapperStyle: CSSProperties | undefined = useMemo(() => {
    if (layout === 'fill') {
      return {
        display: 'block',
        overflow: 'hidden',
        position: 'absolute',
        top: 0,
        left: 0,
        bottom: 0,
        right: 0,
        zIndex: 0,
        // boxSizing: 'border-box',
        // margin: 0,
      }
    } else if (layout === 'responsive') {
      return {
        display: 'block',
        overflow: 'hidden',
        position: 'relative',
        zIndex: 0,
        // boxSizing: 'border-box',
        // margin: 0,
      }
    } else if (layout === 'intrinsic') {
      return {
        display: 'inline-block',
        maxWidth: '100%',
        overflow: 'hidden',
        position: 'relative',
        zIndex: 0,
        // boxSizing: 'border-box',
        // margin: 0,
      }
    } else if (layout === 'fixed') {
      return {
        display: 'inline-block',
        position: 'relative',
        zIndex: 0,
        // boxSizing: 'border-box',
        width,
        height,
      }
    }
  }, [layout, width, height])

  const sizerStyle: CSSProperties | undefined = useMemo(() => {
    if (layout === 'responsive') {
      return {
        display: 'block',
        boxSizing: 'border-box',
        paddingTop: `${(height / width) * 100}%`,
      }
    } else if (layout === 'intrinsic') {
      return { boxSizing: 'border-box', display: 'block', maxWidth: '100%' }
    }
  }, [layout, width, height])

  const sizerSvg = useMemo(() => {
    if (layout === 'intrinsic') {
      return `<svg width="${width}" height="${height}" xmlns="http://www.w3.org/2000/svg" version="1.1"/>`
    }
  }, [layout, width, height])

  const isBase64 = src?.startsWith?.('data:')

  const isLazy = !isBase64 && lazy

  const [inView, setInView] = useState(!isLazy)

  const optimizedSrc = useMemo(() => {
    return isBase64
      ? src
      : src
        ? getOptimizedImage({
          src,
          width,
          // height,
          unoptimized,
          autoPixelRatio,
          cutType,
        })
        : cusDefaultImg
  }, [
    isBase64,
    src,
    width,
    height,
    autoPixelRatio,
    cusDefaultImg,
    cutType,
    unoptimized,
  ])

  const [realSrc, setRealSrc] = useState(() =>
    inView ? optimizedSrc : cusDefaultImg
  )
  const isErrorRef = useRef(false)

  useEffect(() => {
    if (inView && realSrc !== optimizedSrc && !isErrorRef.current) {
      setRealSrc(optimizedSrc)
    }
    isErrorRef.current = false
  }, [inView, realSrc, optimizedSrc])

  const rootRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (!isLazy && !inView) {
      setInView(true)
    }
    if (!isLazy || inView) return
    const observe = new IntersectionObserver(
      (changes) => {
        changes.forEach((change) => {
          if (change.isIntersecting) {
            batchedUpdates(() => {
              setInView(true)
            })
          }
        })
      },
      {
        rootMargin: '100px 0px',
      }
    )
    observe.observe(rootRef.current!)
    return () => {
      observe.disconnect()
    }
  }, [inView, isLazy])

  const handleError = useCallback(() => {
    isErrorRef.current = true
    if (realSrc === optimizedSrc) {
      if (optimizedSrc !== src) {
        setRealSrc(src)
      } else if (optimizedSrc !== errorImg) {
        setRealSrc(errorImg)
      }
    } else if (realSrc === src) {
      if (src !== errorImg) {
        setRealSrc(errorImg)
      }
    }
    logger.error(`[motor-fe/pc/image onError] error message: src=${realSrc}`, {
      src,
    })
    metrics.counter('motor-fe/pc.image.onerror', { realSrc, src })
  }, [src, optimizedSrc, errorImg, realSrc])
  return (
    <div
      style={wrapperStyle}
      ref={rootRef}
      className={className}
      onClick={onClick}
    >
      {sizerStyle ? (
        <div style={sizerStyle}>
          {sizerSvg ? (
            <img
              style={{
                maxWidth: '100%',
                display: 'block',
                margin: 0,
                border: 'none',
                padding: 0,
              }}
              alt=""
              src={`data:image/svg+xml;utf-8,${sizerSvg}`}
              aria-hidden
              role="presentation"
            />
          ) : null}
        </div>
      ) : null}
      {inView ? null : (
        <noscript>
          <img
            src={optimizedSrc}
            alt={alt}
            width={width}
            height={height}
            style={imageStyle}
          />
        </noscript>
      )}
      <img
        src={realSrc}
        alt={alt}
        width={width}
        height={height}
        style={imageStyle}
        onLoad={onLoad}
        onError={handleError}
        className={classNames({
          zoom: hoverTransition,
        })}
      />
    </div>
  )
}

export { ImageStyles }
